// Гласово „Настани резервацията на Здравко" — какво да се направи по пресните
// данни на „Настани" (`GET /reservations/seatable`). Чиста логика без React
// (тестове: voiceSeatPlan.test.ts); изпълнението е в useVoiceSeat.tsx.
//
// Ясен случай → настанява направо: ЕДНА резервация с това име, името е
// СИГУРНО казаното (не само прилича — `surelyNamesGuest`), на ЕДНА маса, и
// единствената възможност е моя отворена сметка или нова моя маса. Всичко
// друго (няколко резервации с това име, само приличащо име, няколко сметки за
// избор, чужда сметка, маси за „Една/Отделна сметка", рано/заето) → модалът
// „Настани" на нея, за да се довърши ръчно. Никакво съвпадение → съобщение.

import type { OrderingSeatTarget, SeatableReservation, SeatableReservations } from "../../../../../api/reservations";
import { seatOptionsFor } from "../seat/seatOptions";

export type VoiceSeatPlan =
  | { kind: "notFound" }
  | { kind: "seat"; reservation: SeatableReservation; target: OrderingSeatTarget }
  | { kind: "manual"; query: string; reservationId: number | null };

const wordsOf = (text: string) =>
  text
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()
    .split(" ")
    .filter(Boolean);

const commonPrefixLength = (left: string, right: string) => {
  let length = 0;
  while (length < left.length && length < right.length && left[length] === right[length]) length += 1;
  return length;
};

// Една дума от името (за търсенето): казано начало („Здрав" → „Здравко") или
// прилича — общ корен от поне 5 букви, различни най-много в последните две
// („Иванови" ↔ „Иванов", но и „Александър" ↔ „Александра" — виж `surelyNamesGuest`).
const sameNameWord = (spokenWord: string, guestWord: string) => {
  if (guestWord.startsWith(spokenWord)) return true;
  const common = commonPrefixLength(spokenWord, guestWord);
  return common >= 5 && common >= Math.max(spokenWord.length, guestWord.length) - 2;
};

// Окончанията на една фамилия: „Иванов" / „Иванова" / „Иванови".
const SURNAME_FORM_ENDINGS = ["", "а", "и"];

// Същата фамилия в друга форма, а не друго име: общият корен е фамилия
// („…ов", „…ев", „…ск"), а остатъците — само „", „а" или „и" („Иванови" ↔
// „Иванов", „Петровски" ↔ „Петровска"; „Александър" ↔ „Александра" — не).
const isSurnameForm = (spokenWord: string, guestWord: string) => {
  const common = commonPrefixLength(spokenWord, guestWord);
  return (
    /(ов|ев|ск)$/.test(spokenWord.slice(0, common)) &&
    SURNAME_FORM_ENDINGS.includes(spokenWord.slice(common)) &&
    SURNAME_FORM_ENDINGS.includes(guestWord.slice(common))
  );
};

/**
 * Казаното е СИГУРНО името на госта, не само прилича: всяка казана дума е
 * цяла дума от името или същата фамилия в друга форма. „Здравко" и „Иванови"
 * при „Николай Иванов" — да; „Иван" при „Ивана"/„Иванов", „Александър" при
 * „Александра", недоизказано „Здрав" — не (те отиват в модала).
 */
const surelyNamesGuest = (guestName: string, spokenName: string) => {
  const guest = wordsOf(guestName);
  return wordsOf(spokenName).every((word) =>
    guest.some((guestWord) => guestWord === word || isSurnameForm(word, guestWord)),
  );
};

/**
 * Казаното име съвпада с госта: всяка казана дума е дума от името (началото
 * ѝ или прилича — `sameNameWord`) — „Здравко" → „Здравко Бонев",
 * „бонев здравко" и „Иванови" → „Николай Иванов" — също.
 */
export const matchesSpokenGuest = (guestName: string, spokenName: string) => {
  const spoken = wordsOf(spokenName);
  const guest = wordsOf(guestName);
  return spoken.length > 0 && spoken.every((word) => guest.some((guestWord) => sameNameWord(word, guestWord)));
};

/**
 * Търсенето, с което се отваря модалът „Настани" при няколко съвпадения — то
 * търси „съдържа текста", затова не казаното („Иванови"), а най-дългата
 * казана дума, сведена до корена, общ с намерените имена („иванов" — и за
 * „Иванов", и за „Иванова").
 */
const listQueryFor = (spokenName: string, matches: SeatableReservation[]) => {
  const longest = wordsOf(spokenName).reduce((best, word) => (word.length > best.length ? word : best), "");
  return matches.reduce((stem, reservation) => {
    const guestWord = wordsOf(reservation.guestName).find((word) => sameNameWord(longest, word));
    return guestWord && !guestWord.startsWith(stem) ? stem.slice(0, commonPrefixLength(stem, guestWord)) : stem;
  }, longest);
};

export const voiceSeatPlan = (data: SeatableReservations, spokenName: string, nowMs: number): VoiceSeatPlan => {
  const matches = data.reservations.filter((reservation) => matchesSpokenGuest(reservation.guestName, spokenName));
  if (matches.length === 0) return { kind: "notFound" };
  if (matches.length > 1) return { kind: "manual", query: listQueryFor(spokenName, matches), reservationId: null };

  const [reservation] = matches;
  const options = seatOptionsFor(reservation, data, nowMs);
  const single = options.kind === "choose" && options.options.length === 1 ? options.options[0] : null;
  if (
    reservation.tableNames.length === 1 &&
    single !== null &&
    single.kind !== "other" &&
    surelyNamesGuest(reservation.guestName, spokenName)
  ) {
    return {
      kind: "seat",
      reservation,
      target: single.kind === "new" ? { newBill: true } : { billId: single.bill.billId },
    };
  }
  // Една резервация — търсенето е пълното ѝ име (модалът я намира, каквато и
  // форма на името да е казана).
  return { kind: "manual", query: reservation.guestName, reservationId: reservation.id };
};
